"use client";

import type { ReactNode } from "react";
import { depthPreviewFor } from "@/lib/depth-preview";

/**
 * DepthPaywall — the shared "paywall-the-depth" surface for every report SKU.
 *
 * Renders a blurred/masked PREVIEW of the paid report's sections (a description of
 * what's locked, never the real computed content — see depth-preview.ts) directly above
 * the unlock CTA, so the seeker sees the depth EXISTS without it being given away.
 *
 * Contract (verified against the offer guards):
 *  - PRESENTATIONAL ONLY. It does NOT fire an impression — the surface keeps its existing
 *    <OfferImpression> with its byte-identical presentationKey (rules 46/47). No re-fire risk.
 *  - The unlock CTA is passed as {children} — DepthPaywall never renders its own button, so
 *    the surface stays single-decision (rule 39): one CTA, intent-matched via rule-40 policy.
 *  - NET-ZERO on the free tier: the host tool keeps rendering its existing free sections; this
 *    only adds a teaser of paid-only depth. No currently-free content is removed or revealed.
 *  - Graceful: no preview config for this planId → render {children} plainly (no wall).
 *
 * Mobile (rules 58/62): the lock uses a STATIC CSS mask + one-shot blur (`.depth-locked`),
 * never a live `backdrop-filter` or animated filter (mobile-INP killer); the preview region
 * carries reserved intrinsic height so the reveal/mount causes no post-tap CLS.
 */
export default function DepthPaywall({
  planId,
  language,
  lockedTitle,
  children,
}: {
  planId: string;
  language: string;
  /** small heading above the locked preview, e.g. "Your full reading also unlocks" */
  lockedTitle?: string;
  /** the single unlock CTA (e.g. the existing PaidReportCTA / ReportTradeUpSelector card) */
  children: ReactNode;
}) {
  const preview = depthPreviewFor(planId, language);
  if (!preview) return <>{children}</>;

  const title =
    lockedTitle ??
    (language === "hi" ? "आपकी पूरी रिपोर्ट में खुलता है" : "Your full reading unlocks");
  const teaser =
    language === "hi"
      ? "पूरी रिपोर्ट में यह भाग आपकी जन्म-कुंडली, दशा और गोचर से निजी रूप से खुलता है।"
      : "The full report personalizes this section from your birth chart, dasha, and transits.";

  return (
    <div className="space-y-4">
      <div className="depth-locked" aria-hidden="true">
        <p className="depth-locked__title">{title}</p>
        <ul className="depth-locked__list">
          {preview.sections.map((s) => (
            <li key={s.label} className="depth-locked__item">
              <p className="depth-locked__label">
                <span className="depth-locked__lock" aria-hidden="true">
                  🔒
                </span>
                {s.label}
              </p>
              {/* shared paid-only depth description, visually masked — reveals nothing real */}
              <p className="depth-locked__teaser">{teaser}</p>
            </li>
          ))}
        </ul>
      </div>
      {children}
    </div>
  );
}
